import { useTogglePopoverModal } from '@lessonup/client-integration';
import {
  Button,
  createModal,
  ModalPopover,
  ModalPopoverAnchor,
  ModalPopoverDirection,
  ModalPopOverParentSelector,
  spacing,
  useModal,
} from '@lessonup/ui-components';
import React, { ComponentProps, PropsWithChildren, useCallback, useRef } from 'react';

export type ModalConfig = {
  modalDirection?: ModalPopoverDirection;
  modalAnchor?: ModalPopoverAnchor;
  contentLabel: string;
  activeWhenOpen?: boolean;
  showStrokeWhenOpen?: boolean;
  preventScrollOutside?: boolean;
  modalBody: (close: CloseHandler) => React.ReactElement;
  onOpen?: () => void;
  onClose?: CloseHandler;
  /** return false if it should not be opened */
  onBeforeOpen?: () => boolean;
};

/** So that the close function can be used to pass data to the parent component */
type OnCloseData = Record<string, unknown>;
type CloseHandler = (data?: OnCloseData) => void;

export interface ButtonWithPopoverProps extends Omit<ComponentProps<typeof Button>, 'onClick' | 'onMouseDown' | 'ref'> {
  modalConfig: ModalConfig;
}

/* ToDo: it would be prefered if this component could live in the ui-components package,
 * but the useTogglePopoverModal hook is not available there at this moment,
 * so teacher-modern is the best place for it for now.
 */

/**
 * A button that opens a toggleable popover with a custom body when clicked.
 * Can be configured to have an active state while open.
 */
export const ButtonWithPopover: React.FC<PropsWithChildren<ButtonWithPopoverProps>> = ({
  modalConfig: {
    modalDirection = 'bottom',
    modalAnchor = 'center',
    contentLabel,
    activeWhenOpen = true,
    showStrokeWhenOpen,
    preventScrollOutside,
    modalBody,
    onOpen,
    onClose,
    onBeforeOpen,
  },
  children,
  ...rest
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const configuredModalComponent = useRef(
    createModalPopoverWithBody(
      modalDirection,
      modalAnchor,
      () => buttonRef.current,
      contentLabel,
      onClose,
      preventScrollOutside
    )
  );

  const modal = useModal(configuredModalComponent.current);

  const [handleOnClickToggle, handleOnMouseDownToggle, isOpen] = useTogglePopoverModal(modal, {
    customModalShow: () => {
      if (onBeforeOpen?.() === false) {
        return;
      }
      onOpen?.();
      modal.show({
        body: modalBody,
      });
    },
    onBeforeClose: onClose,
  });

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleOnClickToggle(e);
    },
    [handleOnClickToggle]
  );

  const handleOnMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleOnMouseDownToggle(e);
    },
    [handleOnMouseDownToggle]
  );

  return (
    <Button
      buttonType="neutral"
      buttonShape="rect"
      active={activeWhenOpen && isOpen}
      showStroke={showStrokeWhenOpen && isOpen}
      ref={buttonRef}
      onClick={handleOnClick}
      onMouseDown={handleOnMouseDown}
      {...rest}
    >
      {children}
    </Button>
  );
};

interface ModalPopoverWithBodyProps {
  body: (close: CloseHandler) => React.ReactElement;
}

function createModalPopoverWithBody(
  direction: ModalPopoverDirection,
  anchor: ModalPopoverAnchor,
  parentSelector: ModalPopOverParentSelector,
  contentLabel: string,
  onClose?: CloseHandler,
  preventScrollOutside?: boolean
) {
  return createModal(({ body }: ModalPopoverWithBodyProps) => {
    const modal = useModal();

    const close = (data?: OnCloseData) => {
      onClose?.(data);
      modal.hide();
    };

    return (
      <ModalPopover
        modal={modal}
        contentLabel={contentLabel}
        direction={direction}
        modalAnchor={anchor}
        parentSelector={parentSelector}
        spaceBetweenParent={spacing.size12}
        onBeforeClose={onClose}
        preventScrollOutside={preventScrollOutside}
      >
        {body(close)}
      </ModalPopover>
    );
  });
}
